// Currency formatting helper. Uses Intl.NumberFormat so $ / £ / € / etc.
// follow the user's locale conventions.
//
// Currency strings are stored lowercase to match Stripe ('usd', 'gbp',
// 'eur'); Intl wants uppercase, so we coerce.

/**
 * Format a cents/pence/öre integer as a localized currency string.
 *
 * @param {number} cents Integer minor-units (USD cents, GBP pence, etc).
 * @param {string} [currency='usd'] ISO 4217, lowercase or uppercase.
 * @returns {string} e.g. "$25.00", "£25.00", "€25,00"
 */
export function formatMoney(cents, currency = 'usd') {
  const amount = (Number(cents) || 0) / 100;
  const code = String(currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    }).format(amount);
  } catch {
    // Unknown currency code or older browser — return a sensible fallback.
    return `${code} ${amount.toFixed(2)}`;
  }
}

/**
 * Just the symbol for a given currency (no amount). Handy for inline labels
 * like "Price ($)" in admin forms where the input is the unsigned amount.
 *
 * @param {string} [currency='usd']
 * @returns {string} '$', '£', '€', etc.
 */
export function currencySymbol(currency = 'usd') {
  const code = String(currency || 'usd').toUpperCase();
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    }).formatToParts(0);
    const sym = parts.find(p => p.type === 'currency')?.value;
    return sym || code;
  } catch {
    return code;
  }
}
